import {
    Context,
    createContext,
    ReactNode,
    useContext,
    useReducer,
    useMemo
} from 'react'

// Interface and const
export interface IError {
    message?: string
    tag?: string
}

export const DEFAULT_ERROR_STATE: IError = {
    message: '',
    tag: '',
}

// Contexts
interface DataContextProps {
    global_error: IError
}

interface ApiContextProps {
    setGlobalError(error: IError): void
    resetGlobalError(): void
}

const DataContext: Context<DataContextProps> = createContext(
    {} as DataContextProps
)

const ApiContext: Context<ApiContextProps> = createContext(
    {} as ApiContextProps
)

export function useAppErrorDataContext() {
    return useContext(DataContext)
}

export function useAppErrorApiContext() {
    return useContext(ApiContext)
}

// Reducers
enum Actions {
    SETMESSAGE = 'SETMESSAGE',
    RESET = 'RESET',
}

interface GlobalError {
    type: Actions
    message?: string
    tag?: string
}

function reducer(state: IError, action: GlobalError) {
    switch (action.type) {
      case Actions.SETMESSAGE:
        return { message: action.message , tag: action.tag }
      case Actions.RESET:
        return { message: '', tag: '' }
      default:
        return state
    }
}

// Provider
export function AppErrorProvider({ children }: { children: ReactNode }) {
    const [global_error_state, dispatch] = useReducer(reducer, DEFAULT_ERROR_STATE);

    const dataCtx: DataContextProps = {
        global_error: global_error_state,
    }

    // we use memo here to prevent the API context consumers from re-rendering
    const apiCtx: ApiContextProps = useMemo(() => {
        const setGlobalError = ({ message, tag } : IError) => {
            dispatch({ 
                type: Actions.SETMESSAGE, 
                message, 
                tag 
            })
        }
        
        const resetGlobalError = () => {
            dispatch({ type: Actions.RESET })
        }

        return { 
            setGlobalError, 
            resetGlobalError 
        }
    }, [])

    return (
        <DataContext.Provider value={dataCtx}>
            <ApiContext.Provider value={apiCtx}>
                {children}
            </ApiContext.Provider>
        </DataContext.Provider>
    )
}
